import React, { useState, useRef, useEffect, useCallback } from "react";
import moment from "moment";
import PropTypes from "prop-types";

import outside from "../../../utilities/outside";
import { cl } from "../../../utilities/cl";
import calcNewElementPosition from "../../../utilities/calcNewElementPosition";
import { MEDIA_BREAKPOINT_LG } from "../../../utilities/breakpoints";

import SvgCalendar from "../../SVG/Calendar";
import Single from "./Single";
import Between from "./Between";
import CheckBox from "../../CheckBox";

const Datepicker = ({
  className = "",
  titles,
  label,
  type,
  variant,
  disabled,
  data = {},
  durationInput,
  format = "DD[.]MM[.]YYYY",
  formatFunc,
  onChange,
}) => {
  const [title] = useState(titles?.main || "");
  const [dates, setDates] = useState("");
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState({});
  const [frozen, setFrozen] = useState({});
  const [lastSelected, setLastSelected] = useState({});
  const [selectedMode, setSelectedMode] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const openRef = useRef(null);
  const contentRef = useRef(null);

  const getDisplayingDates = useCallback(
    (sel) => {
      const formatted = formatDates(sel);
      let result = "";

      if (typeof formatted === "string" && formatted.length) {
        result = formatted;
      } else if (
        typeof formatted === "object" &&
        formatted.from?.length &&
        formatted.from !== "Invalid date"
      ) {
        result = formatted.from;
        if (formatted.to?.length && formatted.to !== "Invalid date") {
          result += ` — ${formatted.to}`;
        }
      }
      return result;
    },
    [format, formatFunc]
  );

  const formatDates = useCallback(
    (sel) => {
      const fmt = format?.length ? format : "DD[.]MM[.]YYYY";
      const fmtFunc =
        typeof formatFunc === "function"
          ? formatFunc
          : (d) => moment(d).format(fmt);

      if (typeof sel === "string" && sel.length && !sel.includes(".")) {
        return fmtFunc(sel);
      }

      if (
        typeof sel === "object" &&
        sel.from?.length &&
        !sel.from.includes(".")
      ) {
        const formatted = { ...sel, from: fmtFunc(sel.from) };
        if (sel.to?.length && !sel.to.includes(".")) {
          formatted.to = fmtFunc(sel.to);
        }
        return formatted;
      }

      return sel;
    },
    [format, formatFunc]
  );

  const hSave = useCallback(
    (sel) => {
      if (typeof onChange === "function") onChange(sel);
      const display = getDisplayingDates(sel);
      setSelected(sel);
      setDates(display);
      setSelectedMode(!!display.length);
    },
    [getDisplayingDates, onChange]
  );

  const hCancel = useCallback(() => {
    hSave(frozen);
  }, [frozen, hSave]);

  const hChange = useCallback(
    (sel) => {
      if (window.outerWidth > MEDIA_BREAKPOINT_LG) {
        hSave(sel);
      } else {
        hSave(sel);
        setLastSelected(sel);
      }
    },
    [hSave]
  );

  const toggleOpened = useCallback(() => {
    if (disabled) return;

    const position = calcNewElementPosition(
      openRef.current,
      contentRef.current,
      "center",
      {
        top: 37,
      }
    );

    setCalendarPosition({
      top: `${position.top}px`,
      left: `${position.left}px`,
    });
    setOpened((prev) => {
      const next = !prev;
      if (next) {
        setFrozen(selected);
        setTimeout(() => document.addEventListener("click", hClose), 0);
      }
      return next;
    });
  }, [disabled, selected]);

  const hClose = useCallback(
    (e) => {
      if (
        e.target === openRef.current ||
        (contentRef.current && openRef.current.contains(e.target))
      ) {
        return;
      }

      if (
        e.target !== contentRef.current &&
        contentRef.current &&
        !contentRef.current.contains(e.target)
      ) {
        setOpened(false);
        document.removeEventListener("click", hClose);
      } else if (e.target.dataset?.role) {
        setOpened(false);
        document.removeEventListener("click", hClose);

        if (e.target.dataset.role === "cancel") hCancel();
        if (e.target.dataset.role === "save") hSave(lastSelected);
      }
    },
    [hCancel, hSave, lastSelected]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("click", hClose);
    };
  }, [hClose]);

  return (
    <div
      className={cl(
        "FormDatepicker",
        { FormDatepicker_icon: variant === "icon" },
        { disabled },
        className
      )}
    >
      {label && <div className="FormDatepicker__label">{label}</div>}

      <div className="FormDatepicker__picker-control">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {data.groupe && (
            <div style={{ marginLeft: -9 }}>
              <CheckBox
                width={200}
                title="Только время окончания"
                activeColor="#ff3347"
                value={data.onlyTo}
                onValueChanged={data.setOnlyTo}
                size="sm1"
              />
            </div>
          )}
          <div
            ref={openRef}
            className={cl("FormDatepicker__open", { selected: selectedMode })}
            onClick={toggleOpened}
          >
            <SvgCalendar variant="dark" size="sm" title={title} />
            <div className="placeholder">{title}</div>
            <div className="date">
              {data.onlyTo
                ? "Дата начала ролика — " + (dates.split(" — ")[1] || "")
                : dates}
            </div>
          </div>
        </div>

        {outside(
          <div
            className={cl("FormDatepicker__wrap", "mobile-modal", {
              opened,
            })}
            style={{ ...calendarPosition }}
          >
            <div
              ref={contentRef}
              className="FormDatepicker__content mobile-modal__content"
            >
              <div className="FormDatepicker__title mobile-modal__title">
                {title}
              </div>

              {type === "single" && (
                <Single
                  oneDay={data.oneDay}
                  oneDate={data.oneDate}
                  disabled={data.disabled}
                  disabledType={data.disabledType}
                  disabledDates={data.disabledDates}
                  disabledPeriod={data.disabledPeriod}
                  default={data.default}
                  title={titles?.from}
                  format={"YYYY[-]MM[-]DD"}
                  onChange={hChange}
                />
              )}

              {type === "between" && (
                <Between
                  oneDay={data.oneDay}
                  oneDate={data.oneDate}
                  disabled={data.disabled}
                  disabledType={data.disabledType}
                  disabledDates={data.disabledDates}
                  disabledPeriod={data.disabledPeriod}
                  default={data.default}
                  hideType={data.onlyTo && "past"}
                  titles={titles}
                  format={"YYYY[-]MM[-]DD"}
                  onChange={hChange}
                  durationInput={durationInput}
                />
              )}

              <div className="FormDatepicker__buttons mobile-modal__buttons">
                <div className="cancel" data-role="cancel">
                  Отменить
                </div>
                <div className="save" data-role="save">
                  Сохранить
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

Datepicker.propTypes = {
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelect: PropTypes.func,
};

export default Datepicker;
